import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import { queryKeys } from '@/lib/api/queryKeys';
import type { AvailableGame, BrandStudioEvent, GameDef } from '@/lib/api/types';

export interface CreateBsEventInput {
  eventId: number;
  sourceTemplateId: number;
}

export type UpdateBsEventInput = Partial<
  Omit<BrandStudioEvent, 'id' | 'eventId' | 'surveyId' | 'sourceTemplateId' | 'survey' | 'event' | 'companyId' | 'status' | 'createdAt' | 'updatedAt'>
>;

export interface SyncStationsResult {
  updated: number;
  surveyId: number | null;
}

export const bsEventApi = {
  getByEvent: (eventId: number) =>
    api.get<BrandStudioEvent | null>(`/bs-event/by-event/${eventId}`),
  get: (id: number) => api.get<BrandStudioEvent>(`/bs-event/${id}`),
  create: (body: CreateBsEventInput) => api.post<BrandStudioEvent>('/bs-event', body),
  update: (id: number, body: UpdateBsEventInput) =>
    api.put<BrandStudioEvent>(`/bs-event/${id}`, body),
  delete: (id: number) => api.delete(`/bs-event/${id}`),
  resetIdentity: (id: number) =>
    api.post<BrandStudioEvent>(`/bs-event/${id}/reset-identity`),
  syncStations: (id: number) =>
    api.post<SyncStationsResult>(`/bs-event/${id}/sync-stations`),
  replaceTemplate: (id: number, sourceTemplateId: number) =>
    api.post<BrandStudioEvent>(`/bs-event/${id}/replace-template`, {
      sourceTemplateId,
    }),
};

// ── Games (catalog + entitlement) ──────────────────────────────────────

export const gamesApi = {
  listCatalog: () => api.get<GameDef[]>('/g-game'),
  listAvailable: () => api.get<AvailableGame[]>('/g-game/available'),
};

export function useAvailableGames() {
  return useQuery({
    queryKey: ['games', 'available'],
    queryFn: () => gamesApi.listAvailable(),
    staleTime: 5 * 60_000,
  });
}

// ── React Query hooks ──────────────────────────────────────────────────

export function useBrandStudioEventByEvent(eventId: number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.bsEvent.byEvent(eventId ?? 0),
    queryFn: () => bsEventApi.getByEvent(eventId!),
    enabled: !!eventId && eventId > 0,
  });
}

export function useCreateBsEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBsEventInput) => bsEventApi.create(body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: queryKeys.bsEvent.byEvent(data.eventId) });
    },
  });
}

export function useUpdateBsEvent(eventId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: UpdateBsEventInput }) =>
      bsEventApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bsEvent.byEvent(eventId) });
    },
  });
}

export function useResetBsEventIdentity(eventId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => bsEventApi.resetIdentity(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bsEvent.byEvent(eventId) });
    },
  });
}

export function useSyncBsEventStations(eventId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => bsEventApi.syncStations(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.stations.byEvent(eventId) });
    },
  });
}

/**
 * Swap the underlying xurvey template of an existing BsEvent — calls the
 * dedicated backend endpoint that clones the new template, repoints the
 * BsEvent's surveyId, and re-propagates to stations. Identity / experience /
 * game overrides on the BsEvent are preserved (only the survey changes).
 */
export function useChangeBsEventTemplate(eventId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      currentId,
      sourceTemplateId,
    }: {
      currentId: number;
      sourceTemplateId: number;
    }) => bsEventApi.replaceTemplate(currentId, sourceTemplateId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.bsEvent.byEvent(eventId) });
      qc.invalidateQueries({ queryKey: queryKeys.stations.byEvent(eventId) });
    },
  });
}
